// =====================================================
// COMPONENTE: TotalBonus
// =====================================================

import { useState, useEffect } from 'react'
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react'
import { formatCurrency } from '../utils/formatCurrency'
import { supabase } from '../services/supabase'
import { mesAtualKey, parseMesFiltro, type MesFiltro } from '../utils/filtroMes'

interface TotalBonusProps {
    className?: string
    mesFiltro?: MesFiltro
}

export function TotalBonus({ className = '', mesFiltro = mesAtualKey() }: TotalBonusProps) {
    const [totalBonus, setTotalBonus] = useState<number>(0)
    const [totalBonusPago, setTotalBonusPago] = useState<number>(0)
    const [totalBonusReceber, setTotalBonusReceber] = useState<number>(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string>('')
    const [variacao, setVariacao] = useState<number>(0)

    useEffect(() => {
        loadTotalBonus()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mesFiltro])

    const loadTotalBonus = async () => {
        try {
            setLoading(true)
            setError('')

            const { data: { user }, error: userError } = await supabase.auth.getUser()

            if (userError || !user) {
                throw new Error('Usuário não autenticado')
            }

            const { data: corretor, error: corretorError } = await supabase
                .from('corretores')
                .select('id')
                .eq('user_id', user.id)
                .single()

            if (corretorError) {
                throw new Error('Corretor não encontrado')
            }

            const { inicio, fim, inicioAnterior, fimAnterior } = parseMesFiltro(mesFiltro)

            const { data: vendas, error: vendasError } = await supabase
                .from('vendas')
                .select('valor_bonus')
                .eq('corretor_id', corretor.id)
                .gte('created_at', inicio.toISOString())
                .lte('created_at', fim.toISOString())
                .order('created_at', { ascending: false })

            if (vendasError) {
                throw vendasError
            }

            const { data: bonusAtual, error: bonusAtualError } = await supabase
                .from('comissoes')
                .select('valor_bonus, data_pagamento')
                .eq('status', 'pago')
                .eq('corretor_id', corretor.id)
                .gte('data_pagamento', inicio.toISOString())
                .lte('data_pagamento', fim.toISOString())
                .order('data_pagamento', { ascending: false })

            const { data: bonusAnterior, error: bonusAnteriorError } = await supabase
                .from('comissoes')
                .select('valor_bonus, data_pagamento')
                .eq('status', 'pago')
                .eq('corretor_id', corretor.id)
                .gte('data_pagamento', inicioAnterior.toISOString())
                .lte('data_pagamento', fimAnterior.toISOString())
                .order('data_pagamento', { ascending: false })

            if (bonusAtualError || bonusAnteriorError) {
                throw bonusAtualError || bonusAnteriorError
            }

            const totalBonusVendas = vendas?.reduce((total, venda) => {
                return total + (venda.valor_bonus || 0)
            }, 0) || 0

            const totalBonusPagoAtual = bonusAtual?.reduce((total, bonus) => {
                return total + (bonus.valor_bonus || 0)
            }, 0) || 0

            const totalBonusPagoAnterior = bonusAnterior?.reduce((total, bonus) => {
                return total + (bonus.valor_bonus || 0)
            }, 0) || 0

            const totalBonusReceberCalculado = Math.max(0, totalBonusVendas - totalBonusPagoAtual)

            setTotalBonus(totalBonusVendas)
            setTotalBonusPago(totalBonusPagoAtual)
            setTotalBonusReceber(totalBonusReceberCalculado)

            let variacaoCalculada = 0
            if (totalBonusPagoAnterior > 0) {
                variacaoCalculada =
                    ((totalBonusPagoAtual - totalBonusPagoAnterior) / totalBonusPagoAnterior) * 100
            } else if (totalBonusPagoAtual > 0) {
                variacaoCalculada = 100
            }

            setVariacao(variacaoCalculada)
        } catch (err) {
            console.error('❌ Erro ao carregar total de bônus:', err)
            setError('Erro ao carregar bônus')
        } finally {
            setLoading(false)
        }
    }

    const formatVariacao = (valor: number) => {
        if (valor === 0) return '0.0%'
        const sinal = valor >= 0 ? '+' : ''
        const valorAbsoluto = Math.abs(valor)
        if (valorAbsoluto >= 100) {
            return `${sinal}${valor.toFixed(0)}%`
        }
        return `${sinal}${valor.toFixed(1)}%`
    }

    const getVariacaoColor = (valor: number) => {
        return valor >= 0 ? 'text-green-600' : 'text-red-600'
    }

    const getVariacaoIcon = (valor: number) => {
        return valor >= 0 ? TrendingUp : TrendingDown
    }

    const VariacaoIcon = getVariacaoIcon(variacao)

    if (loading) {
        return (
            <div className={`bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
                <div className="mb-3 text-right">
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 tracking-wide">Bônus</p>
                </div>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className={`bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
                <div className="mb-3 text-right">
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 tracking-wide">Bônus</p>
                </div>
                <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                        <button
                            onClick={loadTotalBonus}
                            className="mt-2 text-orange-600 dark:text-orange-400 text-sm hover:underline"
                        >
                            Tentar novamente
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={`bg-white dark:bg-gray-800 p-4 lg:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
            <div className="mb-3 text-right">
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 tracking-wide">Bônus</p>
            </div>
            <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <DollarSign className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className={`flex items-center ${getVariacaoColor(variacao)}`}>
                    <VariacaoIcon className="w-4 h-4 mr-1" />
                    <span className="text-sm font-medium">{formatVariacao(variacao)}</span>
                </div>
            </div>
            <div>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(totalBonus)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Total de Bônus</p>

                <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(totalBonusPago)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Bônus Recebidos</p>
                </div>

                {totalBonusReceber > 0 && (
                    <div className="mt-2">
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">
                            A receber: {formatCurrency(totalBonusReceber)}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
